import { useMemo, useState } from "react";
import Skeleton from "@ui-kit/feedback/Skeleton/Skeleton.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Box from "@ui-kit/layout/Box/Box.web";
import Button from "@ui-kit/form/Button/Button.web";
import { ProductTypeSerializer } from "@app-contexts/public/features/products/data/types/products";
import { usePublicBasket } from "@app-contexts/public/features/basket/services/usePublicBasket";
import { componentName } from "@shared/utils/bem";
import useWindowSize from "@shared/hooks/useWindowSize";
import breakpoints from "@shared/theme/props/breakpoints";
import { useTranslation } from "@shared/utils/internationalization";
import useLoadMore from "@shared/hooks/useLoadMore";
import { noop } from "lodash";
import { AddonProductCard } from "./AddonProductCard";
import "./ProductAddonGrid.css";

export interface ProductAddonGridProps {
  products: ProductTypeSerializer[];
  allInPricing?: boolean;
  isLoading?: boolean;
  showAll?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
  onOpenAddonProductModal: () => void;
  setSelectedProduct: (product: ProductTypeSerializer | null) => void;
  analyticsSource?: string;
}

export const PAGE_SIZE = 20;

export const determineTileCount = (width?: number) => {
  if (width && width < breakpoints.xs) {
    return 4;
  }
  if (width && width < breakpoints.sm) {
    return 6;
  }
  return 8;
};

type ResponsiveWidth<
  T = {
    [key in keyof typeof breakpoints | "base"]?: number;
  }
> = T;

export const generateResponsiveTileWidths = (
  responsiveWidth: ResponsiveWidth,
  gap = 20
) => {
  return Object.entries(responsiveWidth).reduce((acc, [key, value]) => {
    acc[key] = `calc((100% - ${(value - 1) * gap}px) / ${value})`;
    return acc;
  }, {} as Record<string, string>);
};

export const ProductAddonGrid = ({
  products,
  allInPricing,
  isLoading = false,
  showAll: showAllDefault,
  hasMore,
  onOpenAddonProductModal,
  onLoadMore,
  setSelectedProduct,
  analyticsSource,
}: ProductAddonGridProps) => {
  const bem = componentName("product-addon-grid");
  const publicBasket = usePublicBasket();
  const [showAll, setShowAll] = useState(showAllDefault);
  const { width } = useWindowSize();
  const tileCount =
    showAll && !isLoading ? products.length : determineTileCount(width);
  const { t } = useTranslation("checkout");

  const responsiveTileWidths = useMemo(
    () => generateResponsiveTileWidths({ base: 2, xs: 3, sm: 4 }),
    []
  );

  const availableProducts = products.filter((product) => !product.sold_out);

  const { loadReference } = useLoadMore({
    handler: onLoadMore ?? noop,
    condition: () => Boolean(hasMore && !isLoading && showAll && onLoadMore),
  });

  const isAlreadyShowingEverything =
    !hasMore && tileCount >= availableProducts.length;
  const showExpandButton =
    !showAll && !showAllDefault && !isAlreadyShowingEverything;

  return (
    <Flex className={bem.className()} data-testid="product-addons-grid">
      {availableProducts.map((product) => (
        <Box
          className={bem.element("product-card-container")}
          key={product.id}
          w={responsiveTileWidths}
        >
          <AddonProductCard
            product={product}
            publicBasket={publicBasket}
            fitToParent
            allInPricing={allInPricing}
            openAddonProductModal={onOpenAddonProductModal}
            setSelectedProduct={setSelectedProduct}
            analyticsSource={analyticsSource}
          />
        </Box>
      ))}

      {isLoading &&
        Array.from({ length: tileCount }).map((_, index) => (
          <Skeleton
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            w={responsiveTileWidths}
            borderRadius="md"
            style={{ aspectRatio: "1 / 1" }}
          />
        ))}

      {showExpandButton && (
        <Button
          variant="outline"
          className={bem.element("load-more-button")}
          onClick={() => setShowAll(true)}
          data-testid="load-more-button"
        >
          {t("See more")}
        </Button>
      )}

      <Box
        ref={loadReference}
        style={{
          height: "1px",
          width: "100%",
          visibility: "hidden",
        }}
        data-testid="load-more-sentinel"
      />
    </Flex>
  );
};
